'use client'

import { ReactNode, useMemo, useState } from 'react'
import type { SellerType } from '@/config/metadata-schemas.config'
import { getMissingRequiredFieldsForResource } from '@/utils/metadata-form.utils'
import {
  DEFAULT_META_FIELD_VIEW,
  getFieldsMatchingView,
  isMetaFieldViewActive,
} from '@/utils/metadata-field-view.utils'
import type { MetaFieldView } from '@/utils/metadata-field-view.utils'
import MetadataResourceForm from './MetadataResourceForm'
import styles from './MetadataManagementTool.module.css'

type ResourceType = MetadataManagementTool.MetadataInstance['resourceType']

type Props = {
  title: string
  resourceType: ResourceType
  instances: MetadataManagementTool.MetadataInstance[]
  values: MetadataManagementTool.Metadata[]
  sellerType: SellerType
  /** The panel's search + filter, passed through to each tier's form. */
  view?: MetaFieldView
}

/**
 * A collapsible container holding every ticket group (or every ticket type)
 * under one heading, with a search box that filters the roster by name. A seller
 * can have dozens of tiers; folding them into one searchable group keeps the
 * panel navigable instead of stacking one top-level section per tier. The badge
 * rolls up the missing-required count across the whole roster, and the container
 * auto-opens when any tier still has gaps. The whole container hides while the
 * panel's search/filter matches no field in any tier.
 */
const MetadataResourceGroup = ({
  title,
  resourceType,
  instances,
  values,
  sellerType,
  view = DEFAULT_META_FIELD_VIEW,
}: Props): ReactNode => {
  const [query, setQuery] = useState('')
  // Open by default so the search box and the (collapsed) tier list are reachable;
  // controlled so typing in the search box doesn't reassert the open state.
  const [open, setOpen] = useState(true)

  const missingCount = useMemo(() => instances.reduce((total, instance) => (
    total + getMissingRequiredFieldsForResource(resourceType, instance.id, values, sellerType).length
  ), 0), [instances, resourceType, values, sellerType])

  const hasViewMatches = !isMetaFieldViewActive(view) || instances.some((instance) => (
    getFieldsMatchingView(resourceType, instance.id, values, sellerType, view).length > 0
  ))

  // Matching the parent group's name too lets a search like "vip" surface
  // every ticket type in the VIP group, not just types literally named "vip".
  const normalizedQuery = query.trim().toLowerCase()
  const filtered = normalizedQuery
    ? instances.filter((instance) => (
      instance.name.toLowerCase().includes(normalizedQuery)
      || instance.groupName?.toLowerCase().includes(normalizedQuery)
    ))
    : instances

  return (
    <details
      className={styles.Resource}
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
      hidden={!hasViewMatches}
      data-testid={`metadata-group-${resourceType}`}
    >
      <summary className={styles.ResourceSummary}>
        <span className={styles.ResourceTitle}>{title}</span>
        {missingCount > 0
          ? (
            <span className={styles.ResourceBadge} data-testid="metadata-group-missing-count">
              {missingCount} required
            </span>
          )
          : (
            <span className={styles.GroupCount} data-testid="metadata-group-count">
              {instances.length}
            </span>
          )}
      </summary>
      <div className={styles.ResourceBody}>
        <input
          type="search"
          className={styles.GroupSearch}
          placeholder={`Search ${title.toLowerCase()}…`}
          aria-label={`Search ${title.toLowerCase()}`}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          data-testid={`metadata-group-search-${resourceType}`}
        />
        {filtered.length === 0
          ? (
            <p className={styles.GroupEmpty} data-testid="metadata-group-empty">
              No matches
            </p>
          )
          : filtered.map((instance) => (
            <MetadataResourceForm
              key={instance.id}
              title={instance.name}
              subtitle={instance.groupName}
              resourceType={resourceType}
              resourceId={instance.id}
              values={values}
              sellerType={sellerType}
              view={view}
            />
          ))}
      </div>
    </details>
  )
}

export default MetadataResourceGroup
